import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useInView } from "react-intersection-observer";
import "./Arena.css";

const Arena: React.FC = () => {
  const [showHexes, setShowHexes] = useState(false);
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.3,
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
      },
    },
  };

  return (
    <section id="arena" className="arena-section" ref={ref}>
      <div className="container">
        <motion.div
          className="arena-content"
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          {/* Header */}
          <motion.div className="arena-header" variants={itemVariants}>
            <h2>The Battlefield Awaits</h2>
            <p>
              One of the most Unique features of Seekers: Shards of Imriel is
              that combat unfolds on a hex-grid arena. Many abilities interact
              with terrain, require direct line of sight, or have strict range
              limits, emphasizing precise positional strategy. Maps feature
              cover, points of interest, and thematic terrain elements that
              shape the flow of battle. Multiple arena layouts offer distinct
              tactical experiences, ranging from tight quarters to open fields
              to objective-driven maps.
            </p>
          </motion.div>

          {/* Arena Showcase */}
          <motion.div className="arena-showcase" variants={itemVariants}>
            <div className="arena-display">
              <div className="map-container">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={showHexes ? "with-hexes" : "no-hexes"}
                    className="map-image"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.5 }}
                    style={{
                      backgroundImage: showHexes
                        ? `url('arena/hexes.webp')`
                        : `url('arena/nohexes.webp')`,
                    }}
                  />
                </AnimatePresence>

                <motion.div
                  className="map-overlay"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: inView ? 1 : 0 }}
                  transition={{ delay: 1, duration: 0.5 }}
                >
                  <div className="overlay-glow overlay-glow-1"></div>
                  <div className="overlay-glow overlay-glow-2"></div>
                  <div className="overlay-glow overlay-glow-3"></div>
                </motion.div>
              </div>

              <div className="arena-controls">
                <motion.button
                  className={`view-toggle ${!showHexes ? "active" : ""}`}
                  onClick={() => setShowHexes(false)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Artwork View
                </motion.button>

                <motion.button
                  className={`view-toggle ${showHexes ? "active" : ""}`}
                  onClick={() => setShowHexes(true)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Hex-Grid
                </motion.button>
              </div>
            </div>

            <div className="arena-title">
              <h3>Featured Arena: The Crypt of Imin'Thar</h3>
              <p>
                A Shard has punctured the ceiling of this crypt and now battle
                unfolds to claim it.
              </p>
            </div>
          </motion.div>

          {/* Features */}
          <motion.div className="arena-features" variants={itemVariants}>
            <div className="features-grid">
              <motion.div
                className="feature-card"
                whileHover={{ scale: 1.05, rotateY: 5 }}
              >
                <div className="feature-icon">⚔️</div>
                <h4>Tactical Combat</h4>
                <p>
                  Every card you play and step you take has strategic
                  consequences
                </p>
              </motion.div>

              <motion.div
                className="feature-card"
                whileHover={{ scale: 1.05, rotateY: 5 }}
              >
                <div className="feature-icon">👁️</div>
                <h4>Line of Sight</h4>
                <p>Use terrain and cover to outmaneuver your opponents</p>
              </motion.div>

              <motion.div
                className="feature-card"
                whileHover={{ scale: 1.05, rotateY: 5 }}
              >
                <div className="feature-icon">🏰</div>
                <h4>Dynamic Maps</h4>
                <p>
                  Each arena offers unique tactical challenges and opportunities
                </p>
              </motion.div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Arena;
